import { createReducer, type Draft } from '@reduxjs/toolkit';

import { createVehicleCollection } from 'Addons/fitmentSearch/fitmentSearchAPI';
import {
  addToGarageRequested,
  discardVehicleFieldsRequested,
  forceVehicleSelectionLater,
  ignoreInexactFields,
  removalFromGarageRequested,
  replaceVehicleValue,
  setGarageRequested,
  vehicleDiscardingRequested,
  vehicleSelected,
  vehicleSpecificationNeeded,
} from 'Core/actions/fitmentSearch/index';
import { garageCleanupRequested, vehicleColorUpdated } from 'Core/actions/fitmentSearch/vehicle';
import { resetRequest } from 'Core/actions/request';
import type { ReduxStateType, ReduxStoreType } from 'Core/store';
import { logRuntimeError } from 'Diagnostics/diagnostics';
import { Vehicle, VehicleCollection } from 'Models/index';
import { mainConfig } from 'Models/uiConfig/componentlessWidgetsConfig';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import type { VehicleValue } from 'Models/vehicle';
import { abortControllerCreator, getCustomerGarage } from 'Modules/serverApi/index';
import { isFunctionalStorageAllowed, subscribeToStoragePermissions } from 'Utils/browserStorage';
import {
  getForceVehicleSelectionLater,
  isLoggedInStateChanged,
  loadGarageData,
  loadSelectedVehicle,
} from 'Utils/userStorage';

const maxGarageSize = 20;

type Storage = {
  garage: VehicleCollection;
  selectedVehicle: string | null;
  forceVehicleSelectionLater: boolean;
  selectedVehicleColorId: string | null | undefined;
};

const emptyState: Storage = {
  garage: VehicleCollection.empty,
  selectedVehicle: null,
  forceVehicleSelectionLater: false,
  selectedVehicleColorId: null,
};

const initState = createInitialState();

export default createReducer(initState, (builder) =>
  builder
    .addCase(vehicleSelected, (draft, { payload }) => {
      if (draft.selectedVehicle !== payload.key) {
        replaceSelectedVehicleAndAddToGarage(draft, () => payload);
      }
    })
    .addCase(vehicleDiscardingRequested, (draft) => {
      setSelectedVehicle(draft, null);
    })
    .addCase(discardVehicleFieldsRequested, (draft, { meta: { fitmentFields, fieldsToDiscard } }) => {
      const selectedVehicle = draft.garage.findByKey(draft.selectedVehicle);
      if (!selectedVehicle) {
        return;
      }

      const newVehicle = new Vehicle(
        selectedVehicle.filter((v) => !fieldsToDiscard.includes(v.field)),
        fitmentFields,
        { ...selectedVehicle.vehicleExtra },
      );

      if (newVehicle.notNull) {
        draft.garage = draft.garage.replace(selectedVehicle.key, newVehicle);
        setSelectedVehicle(draft, newVehicle);
      } else {
        draft.garage = draft.garage.remove(selectedVehicle);
        setSelectedVehicle(draft, null);
      }
    })
    .addCase(vehicleSpecificationNeeded, (draft, { payload: fieldsToSpecify }) => {
      replaceSelectedVehicleAndAddToGarage(draft, (vehicle) =>
        vehicle.concat(
          fieldsToSpecify
            .filter((field) => !vehicle.some((v) => v.field === field))
            .map<VehicleValue>((field) => ({ field, term: '__inexact', value: '__inexact' })),
        ),
      );
    })
    .addCase(ignoreInexactFields, (draft) => {
      replaceSelectedVehicleAndAddToGarage(draft, (vehicle) =>
        vehicle.mapToVehicle((v) => ({ ...v, term: ignoreInexact(v.term), value: ignoreInexact(v.value) })),
      );
    })
    .addCase(garageCleanupRequested, () => emptyState)
    .addCase(vehicleColorUpdated, (state, { payload: { vehicle, vehicleExtra } }) => {
      if (vehicle.key === state.selectedVehicle) {
        vehicle.vehicleExtra = vehicleExtra;
        state.garage = state.garage.replace(vehicle.key, vehicle);
        state.selectedVehicleColorId = vehicle.getVehicleExtraValue('colorId');
      }
    })
    .addCase(setGarageRequested, (draft, { payload: { vehicles, selectVehicle } }) => {
      const selectedVehicleKey = selectVehicle ? vehicles.last?.key : draft.selectedVehicle;
      setSelectedVehicle(draft, vehicles.findByKey(selectedVehicleKey) ?? null);

      draft.garage = getLimitedGarage(vehicles, draft.selectedVehicle);
    })
    .addCase(removalFromGarageRequested, (draft, { payload }) => {
      const selectedVehicle = draft.garage.findByKey(draft.selectedVehicle);
      draft.garage = draft.garage.remove(payload);
      if (selectedVehicle?.equalsBySelection(payload)) {
        setSelectedVehicle(draft, null);
      }
    })
    .addCase(addToGarageRequested, (draft, { payload }) => {
      addVehicleToGarage(draft, payload);
    })
    .addCase(replaceVehicleValue, (draft, { meta: { isolatedKey } }) => {
      if (!isolatedKey) {
        setSelectedVehicle(draft, null);
      }
    })
    .addCase(forceVehicleSelectionLater, (draft) => {
      draft.forceVehicleSelectionLater = true;
    })
    .addCase(resetRequest, (draft, { payload: { discardVehicle } }) => {
      if (discardVehicle) {
        setSelectedVehicle(draft, null);
      }
    }),
);

function createInitialState(): Storage {
  const selectedVehicle = loadSelectedVehicle();
  const garage = loadGarageData();
  const selectedVehicleKey = selectedVehicle.notNull ? selectedVehicle.key : null;
  const vehicles = selectedVehicleKey
    ? garage.replace(selectedVehicleKey, selectedVehicle).add(selectedVehicle)
    : garage;

  return {
    ...emptyState,
    garage: getLimitedGarage(vehicles, selectedVehicleKey),
    selectedVehicle: selectedVehicleKey,
    selectedVehicleColorId: selectedVehicleKey ? selectedVehicle.getVehicleExtraValue('colorId') : null,
    forceVehicleSelectionLater: getForceVehicleSelectionLater() ?? false,
  };
}

function getLimitedGarage(garage: VehicleCollection, selectedVehicleKey: string | null): VehicleCollection {
  if (garage.size <= maxGarageSize) {
    return garage;
  }

  const newestVehicles = garage.slice(-maxGarageSize);
  const selectedVehicle = garage.findByKey(selectedVehicleKey);
  if (selectedVehicle && !newestVehicles.includes(selectedVehicle)) {
    newestVehicles[0] = selectedVehicle;
  }

  return new VehicleCollection(newestVehicles);
}

function addVehicleToGarage(state: Draft<Storage>, vehicle: Vehicle): void {
  state.garage = getLimitedGarage(state.garage.add(vehicle), state.selectedVehicle);
}

function setSelectedVehicle(state: Draft<Storage>, vehicle: Vehicle | null): void {
  state.selectedVehicle = vehicle?.key ?? null;
  state.selectedVehicleColorId = vehicle ? vehicle.getVehicleExtraValue('colorId') : null;
}

function ignoreInexact(v: string) {
  return v === '__inexact' ? '__ignored' : v;
}

const fitmentSearchSelector = (state: ReduxStateType) => state.fitmentSearch;

export function selectedVehicleSelector(state: ReduxStateType): Vehicle {
  const { selectedVehicle, garage } = fitmentSearchSelector(state).storage;
  return garage.findByKey(selectedVehicle) ?? Vehicle.null;
}

export function selectedVehicleColorIdSelector(state: ReduxStateType): string | null | undefined {
  return fitmentSearchSelector(state).storage.selectedVehicleColorId;
}

export function isVehicleSelectedSelector(state: ReduxStateType): boolean {
  return selectedVehicleSelector(state).notNull;
}

export function vehicleInexactSelector(state: ReduxStateType): boolean {
  return selectedVehicleSelector(state).some((v) => v.term === '__inexact' || v.value === '__inexact');
}

export function garageDataSelector(state: ReduxStateType): VehicleCollection {
  return fitmentSearchSelector(state).storage.garage;
}

export function forceVehicleSelectionLaterSelector(state: ReduxStateType): boolean {
  return fitmentSearchSelector(state).storage.forceVehicleSelectionLater;
}

function replaceSelectedVehicleAndAddToGarage(
  draft: Draft<Storage>,
  transform: (selectedVehicle: Vehicle) => Vehicle,
) {
  const { garage } = draft;
  const newVehicle = transform(garage.findByKey(draft.selectedVehicle) ?? Vehicle.null);
  if (!newVehicle.notNull) {
    return;
  }
  const savedVehicle =
    garage.find((vehicle) => vehicle.equals(newVehicle)) ??
    garage.find((vehicle) => vehicle.isMoreSpecificThan(newVehicle));
  if (savedVehicle) {
    setSelectedVehicle(draft, savedVehicle);
    return;
  }

  const lessSpecificVehicle = garage.find((vehicle) => newVehicle.isMoreSpecificThan(vehicle));
  if (lessSpecificVehicle) {
    draft.garage = garage.replace(lessSpecificVehicle.key, newVehicle);
    setSelectedVehicle(draft, newVehicle);
    return;
  }

  for (const vehicle of garage.copy()) {
    const mergedVehicle = vehicle.merge(newVehicle);
    if (mergedVehicle) {
      draft.garage = garage.replace(vehicle.key, mergedVehicle);
      setSelectedVehicle(draft, mergedVehicle);
      return;
    }
  }

  setSelectedVehicle(draft, newVehicle);
  addVehicleToGarage(draft, newVehicle);
}

/**
 * Startup browser data is already in Redux. Capture the saved garage before customer loading,
 * since page edits during the request can overwrite browser storage.
 */
export function initializeVehicleStorage(store: ReduxStoreType): void {
  if (
    !fitmentSearchConfig.isFitmentSearchWidgetsVisible &&
    !mainConfig.Widgets.some(
      (widget) => widget.visible && (widget.type === 'Garage' || widget.type === 'SingleVehicleGarage'),
    )
  ) {
    return;
  }

  const vehicleStorageRequestController = abortControllerCreator();
  const refreshVehicleStorage = async (onInit = false): Promise<void> => {
    const signal = vehicleStorageRequestController.create();
    const storedGarage = onInit ? VehicleCollection.empty : loadGarageData();

    const customerVehicles = await requestConfig
      .getCustomerAsync?.()
      .then((customer) => {
        if (
          signal.aborted ||
          (!isLoggedInStateChanged(!!customer) && onInit && !fitmentSearchConfig.disableVehicleStorage()) ||
          !customer?.id
        ) {
          return;
        }

        return getCustomerGarage(String(customer.id), signal)
          .then((vehicleData) => {
            if (vehicleData && !signal.aborted) {
              return createVehicleCollection(store.getState(), vehicleData.Garage);
            }
          })
          .catch((error) => {
            console.warn(
              "Failed to restore customer's garage:",
              error instanceof Error ? error.message : error,
            );
            logRuntimeError({ source: 'garage_sync', error });
          });
      })
      .catch((error) => {
        console.error("Failed to sync customer's garage", error);
        logRuntimeError({ source: 'garage_sync', error });
      });

    if (signal.aborted) {
      return;
    }

    const currentVehicles = garageDataSelector(store.getState());
    const vehicles = currentVehicles
      .concat(isFunctionalStorageAllowed() ? storedGarage : VehicleCollection.empty)
      .concat(customerVehicles ?? VehicleCollection.empty);

    if (onInit && vehicles.size <= currentVehicles.size) {
      return;
    }

    store.dispatch(setGarageRequested({ vehicles, selectVehicle: false }));
  };

  subscribeToStoragePermissions('functional', () => {
    if (isFunctionalStorageAllowed()) {
      void refreshVehicleStorage();
    }
  });
  refreshVehicleStorage(true);
}
